import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  ArrowLeft, 
  Upload, 
  CheckCircle2, 
  Clock,
  FileText,
  Eye,
  Trash2,
  AlertCircle,
  Lock
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import DashboardLayout from "../../components/DashboardLayout";
import { FloatingTips, TipsBanner } from "../../components/Client/ContextualTips";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DocumentsStep = () => {
  const navigate = useNavigate();
  const { user, isTrialMode } = useAuth();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [documents, setDocuments] = useState([]);
  
  // Check if user is in trial mode (plan_type === 'prueba')
  const isTrialUser = isTrialMode?.() || user?.plan_type === 'prueba';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const caseRes = await axios.get(`${API}/cases/my-case`);
      setCaseData(caseRes.data);
      
      if (caseRes.data?.id) {
        const docsRes = await axios.get(`${API}/cases/${caseRes.data.id}/required-documents`);
        // El endpoint devuelve {documents: [...]} o directamente [...]
        const docsData = docsRes.data?.documents || docsRes.data || [];
        setDocuments(Array.isArray(docsData) ? docsData : []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (docType) => {
    if (!caseData?.id) return;
    
    setUploading(docType);
    try {
      await axios.post(`${API}/cases/${caseData.id}/documents?doc_type=${docType}`);
      toast.success("Documento subido exitosamente");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir documento");
    } finally {
      setUploading(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case "uploaded":
        return <Clock className="w-5 h-5 text-amber-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-navy-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "approved":
        return "Aprobado";
      case "uploaded":
        return "En revisión";
      default:
        return "Pendiente";
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "approved":
        return "badge-success";
      case "uploaded":
        return "badge-warning";
      default:
        return "badge-info";
    }
  };

  const uploadedCount = documents.filter(d => d.status !== "pending").length;
  const progress = documents.length > 0 ? (uploadedCount / documents.length) * 100 : 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gold-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button 
            variant="ghost" 
            onClick={() => navigate("/dashboard")}
            className="mb-4 text-navy-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>
          <h1 className="font-heading text-3xl font-bold text-navy-900 mb-2">
            Recolección de Documentos
          </h1>
          <p className="text-navy-600">
            Sube los documentos requeridos para tu solicitud de visa.
          </p>
        </motion.div>

        {/* Tips Banner */}
        <TipsBanner context="documents" subContext="general" />

        {/* Trial User Warning Banner */}
        {isTrialUser && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl"
          >
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800">Modo Vista Previa</p>
                <p className="text-sm text-amber-700">
                  La subida de documentos está deshabilitada en cuentas de prueba. 
                  Contacta a tu asesor para activar tu cuenta completa.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Progress Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-elevated p-6 mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-navy-900">Progreso de documentación</h3>
              <p className="text-sm text-navy-500">{uploadedCount} de {documents.length} documentos subidos</p>
            </div>
            <span className="text-2xl font-bold text-gold-600">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-3" />
        </motion.div>

        {/* Documents List */}
        <div className="space-y-4">
          {documents.map((doc, index) => (
            <motion.div
              key={doc.doc_type}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              className={`card-elevated p-6 ${
                doc.status === "approved" ? "border-l-4 border-emerald-500" :
                doc.status === "uploaded" ? "border-l-4 border-amber-500" : ""
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    doc.status === "approved" ? "bg-emerald-100" :
                    doc.status === "uploaded" ? "bg-amber-100" : "bg-navy-100"
                  }`}>
                    <FileText className={`w-6 h-6 ${
                      doc.status === "approved" ? "text-emerald-600" :
                      doc.status === "uploaded" ? "text-amber-600" : "text-navy-500"
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy-900">{doc.name}</h3>
                    <p className="text-sm text-navy-500 mt-0.5">{doc.description}</p>
                    <div className="mt-2">
                      <span className={getStatusClass(doc.status)}>
                        {getStatusIcon(doc.status)}
                        <span className="ml-1">{getStatusText(doc.status)}</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {doc.status === "pending" ? (
                    isTrialUser ? (
                      <Button
                        disabled
                        variant="outline"
                        className="text-navy-400 cursor-not-allowed"
                        data-testid={`upload-${doc.doc_type}-btn-disabled`}
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        Bloqueado
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleUpload(doc.doc_type)}
                        disabled={uploading === doc.doc_type}
                        className="bg-gold-600 hover:bg-gold-700 text-white"
                        data-testid={`upload-${doc.doc_type}-btn`}
                      >
                        {uploading === doc.doc_type ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Subir
                          </>
                        )}
                      </Button>
                    )
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm">
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-200"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900">Importante</h4>
              <p className="text-sm text-blue-700 mt-1">
                Asegúrate de que todos los documentos sean legibles y estén en formato PDF o imagen. 
                Nuestro equipo revisará cada documento y te notificará si hay algún problema.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Continue Button */}
        {uploadedCount === documents.length && uploadedCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 text-center"
          >
            <Button
              onClick={() => navigate("/dashboard")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
            >
              <CheckCircle2 className="w-5 h-5 mr-2" />
              Documentación Completa
            </Button>
          </motion.div>
        )}

        {/* Floating Tips */}
        <FloatingTips context="documents" subContext="photo" />
      </div>
    </DashboardLayout>
  );
};

export default DocumentsStep;
